import React, { useState, useEffect, useCallback } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import axios from 'axios';

const RolesManager = () => {
    const [roles, setRoles] = useState([]);
    const [privileges, setPrivileges] = useState([]);

    const [search, setSearch] = useState('');
    const [selectedRole, setSelectedRole] = useState({ name: '', privileges: [] });  // Initialize with default values
    const [dialogOpen, setDialogOpen] = useState(false);

    const token = sessionStorage.getItem('token');

    const fetchRoles = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/roles`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRoles(response.data.roles);
        } catch (error) {
            if (error.response && error.response.status === 403) {
                toast.error("Not enough privileges to fetch roles.");
            } else {
                toast.error("Failed to fetch roles. Please try again.");
            }
            // Optionally log the error for debugging purposes
            console.error(error);
        }
    }, [token]);

    const fetchPrivileges = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/privileges`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPrivileges(response.data.privileges);  // Assuming your API returns an array of privileges
        } catch (error) {
            console.error('Error fetching privileges:', error);
        }
    }, [token]);

    useEffect(() => {
        fetchRoles();
        fetchPrivileges();  // Fetch privileges along with roles
    }, [fetchRoles, fetchPrivileges]);

    const openDialog = (role) => {
        if (role) {
            // Map privilege IDs back to names
            const privilegeNames = role.privileges.map(privilegeId => {
                const privilege = privileges.find(p => p._id === privilegeId);
                return privilege ? privilege.name : privilegeId; // Use ID as fallback
            });
            setSelectedRole({ ...role, privileges: privilegeNames });  // Fill form with role data
        } else {
            setSelectedRole({ name: '', privileges: [] });  // New role, reset form
        }
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
    };

    const handleCreateOrUpdate = async () => {
        if (selectedRole && selectedRole._id) {
            await axios.put(
                `${process.env.REACT_APP_SRV}/roles/`,
                { role: selectedRole },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } else {
            await axios.post(
                `${process.env.REACT_APP_SRV}/roles`,
                { role: selectedRole },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        }
        fetchRoles();
        closeDialog();
    };

    const handleDelete = async (roleID) => {
        await axios.delete(`${process.env.REACT_APP_SRV}/roles/${roleID}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        fetchRoles();
    };

    const handlePrivilegeChange = (privilege) => {
        setSelectedRole(prevData => ({
            ...prevData,
            privileges: prevData.privileges.includes(privilege)
                ? prevData.privileges.filter(p => p !== privilege)
                : [...prevData.privileges, privilege]
        }));
    };

    return (
        <div>
            <ToastContainer/>
            {/* Search and Create Button */}
            <div className="mb-4 flex justify-between items-center">
                <input
                    type="text"
                    placeholder="Search Roles..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="px-4 py-2 bg-gray-800 rounded text-white"
                />
                <button
                    onClick={() => openDialog(null)}
                    className="bg-green-600 px-4 py-2 rounded hover:bg-green-700"
                >
                    Create Role
                </button>
            </div>

            {/* List of Roles */}
            <ul>
                {roles.map((role) => (
                    <li key={role._id} className="bg-gray-800 p-4 mb-2 rounded flex justify-between items-center">
                        <div className="flex items-center">
                            <div>
                                <h3 className="text-xl">{role.name}</h3>  {/* Simple role name display */}
                                {/* Display privilege names as round tags */}
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {role.privileges.map((privilegeId) => {
                                        const privilege = privileges.find(p => p._id === privilegeId);
                                        return (
                                            <span key={privilegeId} className="bg-green-500 text-white px-2 py-1 rounded-full">
                                                {privilege ? privilege.name : 'Unknown'}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="flex">
                            <button
                                onClick={() => openDialog(role)}  // Open dialog in edit mode
                                className="bg-blue-600 px-2 py-1 rounded hover:bg-blue-700 mr-2"
                            >
                                Edit
                            </button>
                            <button
                                onClick={() => handleDelete(role._id)}
                                className="bg-red-600 px-2 py-1 rounded hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </li>
                ))}
            </ul>

            {/* Role Creation/Updating Dialog */}
            {dialogOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center">
                    <div className="bg-gray-800 p-6 rounded-lg">
                        <h3 className="text-xl mb-4">{selectedRole._id ? 'Edit Role' : 'Add New Role'}</h3>
                        <input
                            type="text"
                            placeholder="Role Name"
                            value={selectedRole.name || ''}  // Handle empty value for new roles
                            onChange={(e) => setSelectedRole({ ...selectedRole, name: e.target.value })}
                            className="block w-full mb-2 p-2 bg-gray-700 text-white rounded"
                        />
                        <div className="mb-4">
                            {privileges.map((privilege) => (
                                <label key={privilege._id} className="block mb-2">
                                    <input
                                        type="checkbox"
                                        checked={selectedRole.privileges.includes(privilege.name)}
                                        onChange={() => handlePrivilegeChange(privilege.name)}
                                        className="mr-2"
                                    />
                                    {privilege.name}
                                </label>
                            ))}
                        </div>

                        <div className="flex justify-end">
                            <button
                                onClick={closeDialog}
                                className="bg-red-600 px-4 py-2 rounded hover:bg-red-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateOrUpdate}
                                className="bg-green-600 px-4 py-2 ml-2 rounded hover:bg-green-700"
                            >
                                {selectedRole._id ? 'Update' : 'Create'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RolesManager;
